import React from "react";
import { formatUsPhoneInput, digitsBefore, caretAfterDigits } from "../phoneFormat";
import { phonePlaceholderFor } from "../validation";
import { normalizeAddressType } from "../mailingAddress";

// The one phone input. Round 25.
//
// A COMPONENT rather than a bare hook so that inputMode, the placeholder and
// the address-type gate all travel together with the formatting. A seventh
// phone field added later cannot arrive with one of them missing — the same
// "exactly one place" discipline phoneIssues() already has for the rule itself.
//
// Formats on the US path only. On the APO/FPO/DPO and Foreign paths the value
// passes through untouched: those shapes require no punctuation, so they were
// never broken, and inserting hyphens into "+44 20 7946 0958" would corrupt a
// correctly written number.

export interface PhoneInputProps {
  value: string;
  onChange: (next: string) => void;
  /** The application's addressType — the rule is per APPLICATION, not per field. */
  addressType: unknown;
  id?: string;
  className?: string;
  placeholder?: string;
  autoComplete?: string;
}

export function PhoneInput({
  value,
  onChange,
  addressType,
  id,
  className = "field-input",
  placeholder,
  autoComplete,
}: PhoneInputProps): React.ReactElement {
  const ref = React.useRef<HTMLInputElement>(null);
  const pendingCaret = React.useRef<number | null>(null);
  const isUs = normalizeAddressType(addressType) === "US";

  // Restore the caret AFTER React has written the reformatted value, or the
  // browser would have already parked it at the end of the string.
  React.useLayoutEffect(() => {
    const caret = pendingCaret.current;
    if (caret !== null && ref.current) {
      ref.current.setSelectionRange(caret, caret);
      pendingCaret.current = null;
    }
  });

  function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value;
    if (!isUs) {
      onChange(raw);
      return;
    }
    const caret = e.target.selectionStart ?? raw.length;
    const typedDigits = digitsBefore(raw, caret);
    const formatted = formatUsPhoneInput(raw);
    pendingCaret.current = caretAfterDigits(formatted, typedDigits);
    onChange(formatted);
  }

  /**
   * Repairs a value that was saved BEFORE this round — a draft holding
   * "4105551234" formats itself the moment the field is touched, so nobody has
   * to retype a number they already entered correctly.
   *
   * On blur specifically, not on mount: this only ever rewrites a field the
   * user actually focused, so no stored value changes behind their back.
   */
  function handleBlur(e: React.FocusEvent<HTMLInputElement>): void {
    if (!isUs) return;
    const formatted = formatUsPhoneInput(e.target.value);
    if (formatted !== e.target.value) onChange(formatted);
  }

  return (
    <input
      ref={ref}
      id={id}
      className={className}
      inputMode="tel"
      autoComplete={autoComplete}
      placeholder={placeholder ?? phonePlaceholderFor(addressType)}
      value={value}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
}
